/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { createContext, FC, PropsWithChildren, useState } from 'react'
import { createStore, StoreApi } from 'zustand'
import {
  AuthStoreSettings,
  AuthStoreState,
  UserInformation,
  UserRole,
} from './AuthStoreState'
import { createJSONStorage, persist } from 'zustand/middleware'
import moment from 'moment'
import { LoginData } from './interfaces/LoginData'

export const AuthStoreContext = createContext<StoreApi<AuthStoreState> | null>(null)

interface AuthProviderProps extends PropsWithChildren {
  settings: AuthStoreSettings
}

export const AuthProvider: FC<AuthProviderProps> = ({ children, settings }) => {
  const [store] = useState(() =>
    createStore<AuthStoreState>()(
      persist(
        (set, get) => ({
          settings: settings,
          isAuthenticated: false,
          authorizationData: null,
          userData: null,
          loginData: null,
          pkceValues: null,
          tokenExpirationDate: null,
          setTokenExpirationDate: (date: string | null) =>
            set(state => ({ ...state, tokenExpirationDate: date })),
          setTokenExpiresIn: (expiresIn: number | null) =>
            set(state => ({
              ...state,
              tokenExpirationDate:
                expiresIn == null ? null : moment().add(expiresIn, 'seconds').toString(),
            })),
          setPKCEValues: (values: any) => set(state => ({ ...state, pkceValues: values })),
          handleTokenRefresh: refreshData => {
            set(state => {
              const loginData = get().loginData
              if (loginData == null) {
                throw Error('Cannot update refresh data because login data is null')
              }

              const newLoginData = {
                ...loginData,
                access_token: refreshData.access_token,
                refresh_token: refreshData.refresh_token,
                token_type: refreshData.token_type,
                expires_in: refreshData.expires_in,
              } as LoginData

              if (
                state.user?.accessToken != undefined &&
                state.user.accessToken !== newLoginData.access_token
              ) {
                state.user = {
                  ...state.user,
                  accessToken: newLoginData.access_token,
                }
              }
              return {
                ...state,
                tokenExpirationDate: moment().add(newLoginData.expires_in, 'seconds').toString(),
                loginData: newLoginData,
              }
            })
          },
          setLoginData: (loginData: any) =>
            set(state => {
              const newState = {
                ...state,
                loginData,
              }

              if (loginData.id_token) {
                const loginDataArray = `${loginData?.id_token}`.split('.')
                const info = JSON.parse(atob(loginDataArray[1]))
                const user = {
                  ignitionId: info.sub,
                  mail: info.mail,
                  givenName: info.givenName,
                  surName: info.sn,
                  completeName: info.cn,
                  accessToken: loginData.access_token || 'N/A',
                  roles: [UserRole.USER], // <-- Todos los usuarios tienen rol básico
                } as UserInformation
                newState.user = user
              }

              return newState
            }),
          setAuthorizationData: (values: any) =>
            set(state => ({ ...state, authorizationData: values })),
          setIsAuthenticated: (isAuthenticated: boolean) =>
            set(state => ({ ...state, isAuthenticated })),
          setUserData: (userData: any) => set(state => ({ ...state, userData })),
          clearAuthInfo: () =>
            set(() => ({
              settings: settings,
              isAuthenticated: false,
            })),
        }),
        {
          name: 'authStore',
          storage: createJSONStorage(() => sessionStorage),
        }
      )
    )
  )

  return <AuthStoreContext.Provider value={store}>{children}</AuthStoreContext.Provider>
}
